import React, { useState, useMemo } from 'react';
import { Check, X, Pencil, RefreshCw, Scale, ChevronDown, ChevronRight, ChevronLeft, ChevronUp, ArrowRight, Search, Filter } from 'lucide-react';

export interface SessionDecision {
  decisionId: string;
  sessionId: string;
  projectId: string;
  status: 'pending' | 'accepted' | 'rejected' | 'superseded';
  statement: string;
  topic: string;
  reason: string;
  confidence: 'high' | 'medium';
  speaker: string;
  detectedAt: string;
  transcriptSnippet?: string | null;
  supersedes: string | null;
  supersedesStatement: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  editedStatement: string | null;
  editedTopic: string | null;
}

interface DecisionsTabProps {
  decisions: SessionDecision[];
  loading: boolean;
  scanning: boolean;
  hasTranscript: boolean;
  onAccept: (decisionId: string) => void;
  onReject: (decisionId: string) => void;
  onEditAccept: (decisionId: string, editedStatement: string, editedTopic: string) => void;
  onScan: () => void;
}

const SortIndicator: React.FC<{ isActive: boolean; direction: 'asc' | 'desc' }> = ({ isActive, direction }) => {
  if (!isActive) return null;
  return direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Speaker avatar colors (deterministic by first letter)
const AVATAR_COLORS: Record<string, { bg: string; text: string }> = {
  A: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  B: { bg: 'bg-blue-100', text: 'text-blue-700' },
  C: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  D: { bg: 'bg-violet-100', text: 'text-violet-700' },
  E: { bg: 'bg-rose-100', text: 'text-rose-700' },
  F: { bg: 'bg-amber-100', text: 'text-amber-700' },
  G: { bg: 'bg-teal-100', text: 'text-teal-700' },
  H: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
};

function getAvatarColor(name: string) {
  const letter = (name || '?')[0].toUpperCase();
  return AVATAR_COLORS[letter] || { bg: 'bg-blue-100', text: 'text-blue-700' };
}

// ── Confidence bar visual ───────────────────────────────────────────────────

function ConfidenceBars({ level }: { level: 'high' | 'medium' }) {
  const filled = level === 'high' ? 3 : 2;
  const filledColor = level === 'high' ? 'bg-green-500' : 'bg-amber-500';
  return (
    <span className="inline-flex items-center gap-1 text-slate-600">
      <span className="flex gap-0.5">
        {[0, 1, 2].map(i => (
          <span
            key={i}
            className={`w-1 h-3 rounded-sm ${i < filled ? filledColor : 'bg-slate-200'}`}
          />
        ))}
      </span>
      <span className="text-xs capitalize">{level}</span>
    </span>
  );
}

// ── Pagination ──────────────────────────────────────────────────────────────

function PaginationControls({ page, totalPages, onPageChange, pageSize, onPageSizeChange, totalItems }: {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  pageSize: number;
  onPageSizeChange: (s: number) => void;
  totalItems: number;
}) {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  return (
    <div className="p-4 border-t border-slate-200 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1 text-sm text-slate-600 font-medium">
          <span>Rows:</span>
          <select
            value={pageSize}
            onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}
            className="border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white"
          >
            {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <span className="text-sm text-slate-500">{start}–{end} of {totalItems}</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="w-8 h-8 flex items-center justify-center rounded text-slate-400 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={18} />
        </button>
        {totalPages <= 7 ? (
          Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium ${p === page ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              {p}
            </button>
          ))
        ) : (
          <>
            {[1, 2].map(p => (
              <button key={p} onClick={() => onPageChange(p)} className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium ${p === page ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{p}</button>
            ))}
            {page > 3 && <span className="text-xs text-slate-400 px-1">...</span>}
            {page > 2 && page < totalPages - 1 && (
              <button className="w-8 h-8 flex items-center justify-center rounded text-sm font-medium bg-primary text-white">{page}</button>
            )}
            {page < totalPages - 2 && <span className="text-xs text-slate-400 px-1">...</span>}
            {[totalPages - 1, totalPages].filter(p => p > 2).map(p => (
              <button key={p} onClick={() => onPageChange(p)} className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium ${p === page ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{p}</button>
            ))}
          </>
        )}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="w-8 h-8 flex items-center justify-center rounded text-slate-400 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function statusBadgeClass(status: SessionDecision['status']): string {
  if (status === 'pending') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (status === 'accepted') return 'bg-green-50 text-green-700 border-green-200';
  return 'bg-slate-100 text-slate-500 border-slate-200';
}

type SortField = 'detectedAt' | 'statement' | 'topic' | 'confidence' | 'speaker';

function compareDecisions(a: SessionDecision, b: SessionDecision, field: SortField): number {
  if (field === 'detectedAt') return new Date(a.detectedAt).getTime() - new Date(b.detectedAt).getTime();
  if (field === 'statement') return (a.editedStatement || a.statement).localeCompare(b.editedStatement || b.statement);
  if (field === 'topic') return (a.editedTopic || a.topic).localeCompare(b.editedTopic || b.topic);
  if (field === 'confidence') return a.confidence.localeCompare(b.confidence);
  return a.speaker.localeCompare(b.speaker);
}

// ── Filter / sort hook ──────────────────────────────────────────────────────

function useDecisionsFilter(decisions: SessionDecision[]) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'superseded'>('all');
  const [topicFilter, setTopicFilter] = useState('all');
  const [confidenceFilter, setConfidenceFilter] = useState<'all' | 'high' | 'medium'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<SortField>('detectedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const topics = useMemo(() => [...new Set(decisions.map(d => d.editedTopic || d.topic))].sort(), [decisions]);

  const filtered = useMemo(() => {
    let result = decisions.filter(d => d.status !== 'rejected');

    if (statusFilter !== 'all') result = result.filter(d => d.status === statusFilter);
    if (topicFilter !== 'all') result = result.filter(d => (d.editedTopic || d.topic) === topicFilter);
    if (confidenceFilter !== 'all') result = result.filter(d => d.confidence === confidenceFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d =>
        (d.editedStatement || d.statement).toLowerCase().includes(q) ||
        (d.editedTopic || d.topic).toLowerCase().includes(q) ||
        d.speaker.toLowerCase().includes(q) ||
        (d.reason || '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      const cmp = compareDecisions(a, b, sortField);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [decisions, statusFilter, topicFilter, confidenceFilter, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeP = Math.min(page, totalPages);
  const paged = filtered.slice((safeP - 1) * pageSize, safeP * pageSize);

  const activeFilters = (statusFilter !== 'all' ? 1 : 0) + (topicFilter !== 'all' ? 1 : 0) + (confidenceFilter !== 'all' ? 1 : 0);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'detectedAt' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  return {
    search, setSearch,
    statusFilter, setStatusFilter,
    topicFilter, setTopicFilter,
    confidenceFilter, setConfidenceFilter,
    showFilters, setShowFilters,
    page, setPage,
    pageSize, setPageSize,
    sortField, sortDir, handleSort,
    topics, filtered, paged, totalPages, safeP, activeFilters,
  };
}

// ── Filter bar / pills ──────────────────────────────────────────────────────

interface DecisionsFilterBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  activeFilters: number;
  statusFilter: 'all' | 'pending' | 'accepted' | 'superseded';
  onStatusFilterChange: (v: 'all' | 'pending' | 'accepted' | 'superseded') => void;
  topicFilter: string;
  onTopicFilterChange: (v: string) => void;
  topics: string[];
  confidenceFilter: 'all' | 'high' | 'medium';
  onConfidenceFilterChange: (v: 'all' | 'high' | 'medium') => void;
  onClearFilters: () => void;
  pills: React.ReactNode;
}

const DecisionsFilterBar: React.FC<DecisionsFilterBarProps> = ({
  search, onSearchChange, showFilters, onToggleFilters, activeFilters,
  statusFilter, onStatusFilterChange, topicFilter, onTopicFilterChange, topics,
  confidenceFilter, onConfidenceFilterChange, onClearFilters, pills,
}) => (
  <>
    <div className="flex items-center gap-3 mb-4">
      <div className="relative flex-1 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search decisions..."
          className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all"
        />
      </div>
      <button
        onClick={onToggleFilters}
        className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
          showFilters || activeFilters > 0
            ? 'border-primary bg-primary/5 text-primary'
            : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
        }`}
      >
        <Filter size={16} />
        Filters{activeFilters > 0 && ` (${activeFilters})`}
      </button>
    </div>

    {pills}

    {showFilters && (
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-sm text-slate-600 font-medium">Status</label>
            <select
              value={statusFilter}
              onChange={e => onStatusFilterChange(e.target.value as any)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white hover:bg-slate-50 transition-colors"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="superseded">Superseded</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-sm text-slate-600 font-medium">Topic</label>
            <select
              value={topicFilter}
              onChange={e => onTopicFilterChange(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white hover:bg-slate-50 max-w-[180px] transition-colors"
            >
              <option value="all">All</option>
              {topics.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-sm text-slate-600 font-medium">Confidence</label>
            <select
              value={confidenceFilter}
              onChange={e => onConfidenceFilterChange(e.target.value as any)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white hover:bg-slate-50 transition-colors"
            >
              <option value="all">All</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
            </select>
          </div>
        </div>
        {activeFilters > 0 && (
          <button
            onClick={onClearFilters}
            className="text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>
    )}
  </>
);

interface DecisionsFilterPillsProps {
  pendingCount: number;
  acceptedCount: number;
  supersededCount: number;
  statusFilter: 'all' | 'pending' | 'accepted' | 'superseded';
  onToggleStatus: (status: 'pending' | 'accepted' | 'superseded') => void;
}

const DecisionsFilterPills: React.FC<DecisionsFilterPillsProps> = ({
  pendingCount, acceptedCount, supersededCount, statusFilter, onToggleStatus,
}) => (
  <div className="flex items-center gap-2 flex-wrap">
    {pendingCount > 0 && (
      <button
        onClick={() => onToggleStatus('pending')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
          statusFilter === 'pending'
            ? 'bg-amber-100 text-amber-700 border-amber-300'
            : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
        }`}
      >
        {pendingCount} pending
      </button>
    )}
    {acceptedCount > 0 && (
      <button
        onClick={() => onToggleStatus('accepted')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
          statusFilter === 'accepted'
            ? 'bg-green-100 text-green-700 border-green-300'
            : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
        }`}
      >
        {acceptedCount} accepted
      </button>
    )}
    {supersededCount > 0 && (
      <button
        onClick={() => onToggleStatus('superseded')}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
          statusFilter === 'superseded'
            ? 'bg-slate-200 text-slate-700 border-slate-300'
            : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
        }`}
      >
        {supersededCount} superseded
      </button>
    )}
  </div>
);

// ── Main Component ──────────────────────────────────────────────────────────

const DecisionsTab: React.FC<DecisionsTabProps> = ({
  decisions,
  loading,
  scanning,
  hasTranscript,
  onAccept,
  onReject,
  onEditAccept,
  onScan,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStatement, setEditStatement] = useState('');
  const [editTopic, setEditTopic] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const {
    search, setSearch,
    statusFilter, setStatusFilter,
    topicFilter, setTopicFilter,
    confidenceFilter, setConfidenceFilter,
    showFilters, setShowFilters,
    page, setPage,
    pageSize, setPageSize,
    sortField, sortDir, handleSort,
    topics, filtered, paged, totalPages, safeP, activeFilters,
  } = useDecisionsFilter(decisions);

  const nonRejected = decisions.filter(d => d.status !== 'rejected');
  const pendingCount = decisions.filter(d => d.status === 'pending').length;
  const acceptedCount = decisions.filter(d => d.status === 'accepted').length;
  const supersededCount = decisions.filter(d => d.status === 'superseded').length;

  const startEdit = (d: SessionDecision) => {
    setEditingId(d.decisionId);
    setEditStatement(d.statement);
    setEditTopic(d.topic);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditStatement('');
    setEditTopic('');
  };

  const submitEdit = (decisionId: string) => {
    onEditAccept(decisionId, editStatement, editTopic);
    setEditingId(null);
  };

  // Empty state
  if (!loading && decisions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
        <Scale size={48} className="text-slate-300 mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">No decisions detected yet</h3>
        <p className="text-sm text-slate-500 mb-6 max-w-sm">
          Decisions will appear here as they're identified from your session conversation.
        </p>
        <button
          onClick={onScan}
          disabled={scanning || !hasTranscript}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-primary text-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={14} className={scanning ? 'animate-spin' : ''} />
          {scanning ? 'Scanning...' : 'Scan Now'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-y-auto bg-slate-50/50 p-4">
      {/* White card container */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden max-w-[1024px] w-full mx-auto">

        {/* Header Section */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-slate-900">
                Decisions <span className="text-slate-400 text-base font-normal">({nonRejected.length})</span>
              </h2>
              {pendingCount > 0 && (
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  {pendingCount} pending
                </span>
              )}
            </div>
            <button
              onClick={onScan}
              disabled={scanning || !hasTranscript}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw size={14} className={scanning ? 'animate-spin' : ''} />
              {scanning ? 'Scanning...' : 'Scan Now'}
            </button>
          </div>

          {/* Search + Filter toggle + status pills + dropdown filters */}
          <DecisionsFilterBar
            search={search}
            onSearchChange={v => { setSearch(v); setPage(1); }}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            activeFilters={activeFilters}
            statusFilter={statusFilter}
            onStatusFilterChange={v => { setStatusFilter(v); setPage(1); }}
            topicFilter={topicFilter}
            onTopicFilterChange={v => { setTopicFilter(v); setPage(1); }}
            topics={topics}
            confidenceFilter={confidenceFilter}
            onConfidenceFilterChange={v => { setConfidenceFilter(v); setPage(1); }}
            onClearFilters={() => { setStatusFilter('all'); setTopicFilter('all'); setConfidenceFilter('all'); setPage(1); }}
            pills={
              <DecisionsFilterPills
                pendingCount={pendingCount}
                acceptedCount={acceptedCount}
                supersededCount={supersededCount}
                statusFilter={statusFilter}
                onToggleStatus={status => { setStatusFilter(statusFilter === status ? 'all' : status); setPage(1); }}
              />
            }
          />
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={20} className="animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50">
                    <th className="w-10 px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider" />
                    <th className="px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider w-24">Status</th>
                    <th
                      className="px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('statement')}
                    >
                      <span className="inline-flex items-center gap-1">Decision <SortIndicator isActive={sortField === 'statement'} direction={sortDir} /></span>
                    </th>
                    <th
                      className="px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider w-32 cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('topic')}
                    >
                      <span className="inline-flex items-center gap-1">Topic <SortIndicator isActive={sortField === 'topic'} direction={sortDir} /></span>
                    </th>
                    <th
                      className="px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider w-32 cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('confidence')}
                    >
                      <span className="inline-flex items-center gap-1">Confidence <SortIndicator isActive={sortField === 'confidence'} direction={sortDir} /></span>
                    </th>
                    <th
                      className="px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider w-32 cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('speaker')}
                    >
                      <span className="inline-flex items-center gap-1">Speaker <SortIndicator isActive={sortField === 'speaker'} direction={sortDir} /></span>
                    </th>
                    <th
                      className="px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider w-24 cursor-pointer hover:text-slate-700 select-none"
                      onClick={() => handleSort('detectedAt')}
                    >
                      <span className="inline-flex items-center gap-1">Time <SortIndicator isActive={sortField === 'detectedAt'} direction={sortDir} /></span>
                    </th>
                    <th className="px-4 py-3 text-slate-500 font-medium text-xs uppercase tracking-wider text-right w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-slate-100">
                  {paged.map(d => {
                    const isExpanded = expandedId === d.decisionId;
                    const isEditing = editingId === d.decisionId;
                    const isSuperseded = d.status === 'superseded';
                    const avatar = getAvatarColor(d.speaker);

                    const statusStyles = statusBadgeClass(d.status);

                    return (
                      <React.Fragment key={d.decisionId}>
                        <tr
                          className={`hover:bg-slate-50 transition-colors group ${isSuperseded ? 'opacity-60' : ''} ${isExpanded ? 'bg-slate-50/50' : ''}`}
                          style={{ cursor: isEditing ? 'default' : 'pointer' }}
                          onClick={() => !isEditing && setExpandedId(isExpanded ? null : d.decisionId)}
                        >
                          {/* Expand chevron */}
                          <td className="px-4 py-3 text-slate-400">
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </td>
                          {/* Status */}
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border capitalize ${statusStyles}`}>
                              {d.status}
                            </span>
                          </td>
                          {/* Decision */}
                          <td className={`px-4 py-3 font-medium text-slate-900 ${isSuperseded ? 'line-through text-slate-400' : ''}`}>
                            {d.editedStatement || d.statement}
                          </td>
                          {/* Topic */}
                          <td className="px-4 py-3 text-slate-600">{d.editedTopic || d.topic}</td>
                          {/* Confidence */}
                          <td className="px-4 py-3">
                            <ConfidenceBars level={d.confidence} />
                          </td>
                          {/* Speaker */}
                          <td className="px-4 py-3 text-slate-600">
                            <span className="flex items-center gap-2">
                              <span className={`w-5 h-5 rounded-full ${avatar.bg} ${avatar.text} flex items-center justify-center text-xs font-bold shrink-0`}>
                                {(d.speaker || '?')[0].toUpperCase()}
                              </span>
                              {d.speaker}
                            </span>
                          </td>
                          {/* Time */}
                          <td className="px-4 py-3 text-slate-500">{timeAgo(d.detectedAt)}</td>
                          {/* Actions */}
                          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                            {d.status === 'pending' && (
                              <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => onAccept(d.decisionId)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="Accept"
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  onClick={() => startEdit(d)}
                                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                                  title="Edit & Accept"
                                >
                                  <Pencil size={15} />
                                </button>
                                <button
                                  onClick={() => onReject(d.decisionId)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                  title="Reject"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            )}
                            {d.status === 'accepted' && (
                              <div className="text-right">
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">
                                  Current
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>

                        {/* Expanded detail row */}
                        {isExpanded && !isEditing && (
                          <tr>
                            <td colSpan={8} className="py-0">
                              <div className="ml-10 mr-4 my-2 bg-slate-50 rounded-lg p-4">
                                {d.transcriptSnippet && (
                                  <p className="text-xs italic text-slate-500 mb-2 border-l-2 border-slate-200 pl-3 line-clamp-3">
                                    "{d.transcriptSnippet}"
                                  </p>
                                )}
                                {d.supersedesStatement && (
                                  <div className="flex items-center gap-1.5 text-xs text-orange-600 mb-2">
                                    <ArrowRight size={12} />
                                    May supersede: <span className="font-medium">{d.supersedesStatement}</span>
                                  </div>
                                )}
                                {d.reason && (
                                  <p className="text-xs text-slate-500 mb-2">
                                    <span className="font-medium text-slate-600">Reason:</span> {d.reason}
                                  </p>
                                )}
                                <div className="text-[11px] text-slate-400 mt-1">
                                  Detected {new Date(d.detectedAt).toLocaleString()}
                                  {d.acceptedAt && <> &middot; Accepted {new Date(d.acceptedAt).toLocaleString()}</>}
                                  {d.acceptedBy && <> by {d.acceptedBy}</>}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Edit row */}
                        {isEditing && (
                          <tr>
                            <td colSpan={8} className="py-0">
                              <div className="ml-10 mr-4 my-2 bg-slate-50 rounded-lg p-4 flex flex-col gap-3">
                                <div>
                                  <label className="text-xs font-medium text-slate-500 mb-1 block">Statement</label>
                                  <textarea
                                    value={editStatement}
                                    onChange={e => setEditStatement(e.target.value)}
                                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none outline-none"
                                    rows={2}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-slate-500 mb-1 block">Topic</label>
                                  <input
                                    value={editTopic}
                                    onChange={e => setEditTopic(e.target.value)}
                                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => submitEdit(d.decisionId)}
                                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                                  >
                                    <Check size={14} /> Save & Accept
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="px-4 py-2 text-sm font-medium rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {paged.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400 text-sm">
                        No decisions match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtered.length > pageSize && (
              <PaginationControls
                page={safeP}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                totalItems={filtered.length}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DecisionsTab;
